import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import copy from "clipboard-copy";
import axios from "../../axios";
import { shareOrCopy } from "../../utils/share";
import PageShell from "../../components/ui/PageShell";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import StoryIllustration from "../../components/StoryIllustration";

const PublicResult = () => {
  const { resultId } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedText, setCopiedText] = useState(false);
  const [libraryInfo, setLibraryInfo] = useState(null);

  useEffect(() => {
    const fetchResult = async () => {
      try {
        const res = await axios.get(`/story/result/${resultId}`);
        if (res.data.success && res.data.result) {
          setResult(res.data.result);
          // Fetch library info if premadeTextId exists
          const ptid = res.data.result.story?.premadeTextId;
          if (ptid) {
            try {
              const libRes = await axios.get(`/library/texts/${ptid}`);
              if (libRes.data.success) setLibraryInfo(libRes.data.text);
            } catch (e) {}
          }
        } else {
          setError("Story not found");
        }
      } catch (err) {
        if (err.response?.status === 404) {
          setError("Story not found");
        } else {
          setError("Couldn't load story. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchResult();
  }, [resultId]);

  const shareUrl = `${window.location.origin}/result/${resultId}`;

  const handleCopy = () => {
    copy(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    const teaser = result?.resultText && result.resultText.length > 100
      ? result.resultText.slice(0, 100) + "…"
      : (result?.resultText || "Check out this Glad Libs story!");
    const { method } = await shareOrCopy({
      title: result?.title ? `"${result.title}" — Glad Libs` : "A Glad Libs story",
      text: teaser,
      url: shareUrl,
    });
    if (method === "copied") {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <PageShell>
        <Card>
          <div className="loader" style={{ margin: "0 auto" }}></div>
        </Card>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell>
        <Card>
          <h1 className="ui-heading">{error}</h1>
          <Button onClick={() => navigate("/home")}>Go to Home</Button>
        </Card>
      </PageShell>
    );
  }

  const date = result.createdAt
    ? new Date(result.createdAt).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null;

  return (
    <PageShell>
      <Card>
        <h1
          className="ui-heading"
          style={{
            backgroundColor: "#1f2937",
            color: "#ffffff",
            padding: "var(--spacing-md)",
            borderRadius: "var(--radius-md)",
            marginBottom: "var(--spacing-lg)",
          }}
        >
          {result.title || "Untitled story"}
        </h1>
        {libraryInfo && (
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)', fontStyle: 'italic', textAlign: 'center', marginTop: '-8px', marginBottom: 'var(--spacing-md)' }}>
            Based on {libraryInfo.title} by {libraryInfo.author}{libraryInfo.year ? ` (${libraryInfo.year})` : ''}
          </p>
        )}
        {date && (
          <p
            style={{
              fontSize: "14px",
              color: "var(--text-secondary)",
              marginBottom: "var(--spacing-lg)",
              textAlign: "center",
            }}
          >
            {date}
          </p>
        )}
        <div
          style={{
            fontSize: "18px",
            lineHeight: "1.8",
            color: "var(--text-primary)",
            padding: "var(--spacing-lg)",
            minHeight: "100px",
            whiteSpace: "pre-wrap",
            wordWrap: "break-word",
          }}
        >
          {result.resultText}
        </div>
        <StoryIllustration resultId={resultId} title={result?.title} />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "var(--spacing-md)",
            marginTop: "var(--spacing-lg)",
          }}
        >
          <Button onClick={handleShare} style={{ maxWidth: "160px" }}>
            Share
          </Button>
          <Button
            onClick={handleCopy}
            style={{
              maxWidth: "160px",
              backgroundColor: "transparent",
              color: "var(--text-primary)",
              border: "1px solid var(--border-color)",
            }}
          >
            {copied ? "Copied!" : "Copy link"}
          </Button>
          <Button
            onClick={() => {
              copy(result?.resultText || "");
              setCopiedText(true);
              setTimeout(() => setCopiedText(false), 2000);
            }}
            style={{
              maxWidth: "160px",
              backgroundColor: "transparent",
              color: "var(--text-primary)",
              border: "1px solid var(--border-color)",
            }}
          >
            {copiedText ? "Copied!" : "Copy story"}
          </Button>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "var(--spacing-lg)",
          }}
        >
          {libraryInfo && (
            <button
              onClick={() => navigate(`/library/${libraryInfo.textId}/edit`)}
              style={{ background: 'none', border: 'none', color: 'var(--color-primary)', fontSize: '15px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500, marginRight: 'var(--spacing-md)' }}
              onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
              onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
            >
              Make your own version →
            </button>
          )}
          <Button
            variant="tertiary"
            onClick={() => navigate("/home")}
            style={{ width: "auto" }}
          >
            Play your own Glad Libs
          </Button>
        </div>
      </Card>
    </PageShell>
  );
};

export default PublicResult;
